import { defineStore } from "pinia";
import { AnalyticsV2ElementType } from "@/models/etc/types";
import { CommonHttp } from "@/models/interfaces/http/common.http.request";
import { rootStore } from "./index";
import { MultiEditEntityContainer, MultiEditEntityFromListRemoveRequest, MultiEditEntityToListAddRequest, MultiEditParameterListAsker, MultiEditParameterValueEditAsker, MultiEditStorageNoteEditRequest, MultiEditStorageNoteEditType, MultiEditTagAddAsker, MultiEditTagAsker, MultiEditTagStatusEditAsker, PinnedEntitiesSetRequest } from "./interfaces/types.analytics";
import { generalStore } from "./general";

/**
 * multiedit methods store
 */

const API_URL = process.env.VUE_APP_API_URL

export type MultiEditState = {
};

export const multiEditStore = defineStore({
  id: "multiEditStore",
  state: () =>
    ({} as MultiEditState),

  actions: {
    async getParametersContainer(segment?: AnalyticsV2ElementType) {
      const rootstore = rootStore()
      const targetSegment = segment ?? rootstore.generalMode
      if (targetSegment !== AnalyticsV2ElementType.Client &&
        targetSegment !== AnalyticsV2ElementType.Project &&
        targetSegment !== AnalyticsV2ElementType.StorageItem) {
        return undefined
      }

      const http = new CommonHttp()
      try {
        const response = await http.get(`Analytics/MultiEdit/GetParameters/?segment=${targetSegment}`);
        if (response.status === 200) {
          
          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return undefined
    },

    /**
     * addTags method. Backend API method for adding tags on a list of entites
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async addTags(arg: MultiEditTagAddAsker, no_indication: boolean = false) {
      if (arg.Container.Segment === AnalyticsV2ElementType.Tag) {
        return
      }

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const http_url = "Analytics/MultiEdit/AddTags";

        // console.log(arg)
        const response = await http.put(http_url, arg);
        // console.log(response)
        if (response.status === 200) {
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * deleteTags method. Backend API method for deleting existing tags from a list of entites
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async deleteTags(arg: MultiEditTagAsker, no_indication: boolean = false) {
      if (arg.Container.Segment === AnalyticsV2ElementType.Tag) {
        return
      }

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const http_url = "Analytics/MultiEdit/RemoveTags";

        // console.log(arg)
        const response = await http.put(http_url, arg);
        // console.log(response)
        if (response.status === 200) {
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * addEntitiesToList method. Backend API method for adding entities to an analytics list
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async addEntitiesToList(arg: MultiEditEntityToListAddRequest, no_indication: boolean = false) {
      const rootstore = rootStore();
      const genstore = generalStore();
      const http = new CommonHttp();
      try {
        const http_url = "Analytics/MultiEdit/AddEntitiesToList";
        const response = await http.put(http_url, arg);
        // console.log(response)
        if (response.status === 200) {

          // refresh if a new list was created
          if (!arg.ListId || arg.ListId <= 0) {
            setTimeout(async () => {
              // for edit later; may be a collision
              // that.selectedAfter = reactive({} as BaseListItem);
              await genstore.getGeneralAnalyticsData(rootstore.generalMode)
            }, 1000)
          }

          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * deleteEntitiesFromList method. Backend API method for deleting existing entities from an analytics list
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async deleteEntitiesFromList(arg: MultiEditEntityFromListRemoveRequest, no_indication: boolean = false) {
      const rootstore = rootStore();
      const http = new CommonHttp();
      try {
        const http_url = "Analytics/MultiEdit/RemoveEntitiesFromList";
        const response = await http.put(http_url, arg);
        if (response.status === 200) {
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * changeTagsStatus method. Backend API method for changing existing tags status (standard, hidden)
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async changeTagsStatus(arg: MultiEditTagStatusEditAsker, no_indication: boolean = false) {
      if (arg.Container.Segment !== AnalyticsV2ElementType.Tag) {
        return
      }

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const http_url = "Analytics/MultiEdit/EditTagStatus";

        // console.log(arg)
        const response = await http.put(http_url, arg);
        // console.log(response)
        if (response.status === 200) {
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * changeStorageItemsComment method. Backend API method for changing existing tags status (standard, hidden)
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async changeStorageItemsComment(arg: MultiEditStorageNoteEditRequest, no_indication: boolean = false) {
      if (arg.Container.Segment !== AnalyticsV2ElementType.StorageItem) {
        return
      }

      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        const http_url = "Analytics/MultiEdit/StorageItemNotes";

        // console.log(arg)
        const response = await http.put(http_url, arg);
        // console.log(response)
        if (response.status === 200) {
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * changeItemMovementsComment method. Backend API method for changing existing tags status (standard, hidden)
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async changeItemMovementsComment(arg: MultiEditStorageNoteEditRequest, no_indication: boolean = false) {
      if (arg.Container.Segment !== AnalyticsV2ElementType.StorageItemMovement ||
          arg.ActionType !== MultiEditStorageNoteEditType.Append
      ) {
        return
      }

      const rootstore = rootStore()
      const http = new CommonHttp(API_URL, rootstore.globalJWT)
      try {
        const http_url = "Analytics/MultiEdit/StorageItemMovementNotes";

        // console.log(arg)
        const response = await http.put(http_url, arg);
        // console.log(response)
        if (response.status === 200) {
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * changePinStatus method. Backend API method for changing existing pinned entities status
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async changePinStatus(arg: PinnedEntitiesSetRequest, no_indication: boolean = false) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const http_url = "Analytics/General/SetPinnedEntities";

        // console.log(arg)
        const response = await http.put(http_url, arg);
        // console.log(response)
        if (response.status === 200) {
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * preCheckRemoveAccess method. Backend API method for checking if the user has rights to remove entities
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async preCheckRemoveAccess(arg: MultiEditEntityContainer) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.post(`Analytics/MultiEdit/PreCheckRemoveAccess`, arg);
        if (response.status === 200) {

          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    /**
     * removeEntities method. Backend API method for removing existing entities
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async removeEntities(arg: MultiEditEntityContainer, no_indication: boolean = false) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const http_url = "Analytics/MultiEdit/RemoveEntities";

        // console.log(arg)
        const response = await http.post(http_url, arg);
        // console.log(response)
        if (response.status === 200) {
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * preCheckRemoveAccess method. Backend API method for checking if the user has rights to remove entities
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async preCheckParametersAccess(arg: MultiEditEntityContainer) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.post(`Analytics/MultiEdit/PreCheckParameterAccess`, arg);
        if (response.status === 200) {

          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    /**
     * getValueSetHints method. Backend API method for getting hits for value set type parameters
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async getValueSetHints(valueSetId: number) {
      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const response = await http.get(`Analytics/MultiEdit/GetDefaultHints?valueSetId=`+valueSetId);
        if (response.status === 200) {

          const { data } = response;
          return { data }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { data: undefined }
    },

    /**
     * deleteParameters method. Backend API method for deleting existing parameters attached to entites
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async deleteParameters(arg: MultiEditParameterListAsker, no_indication: boolean = false) {
      if (arg.Container.Segment !== AnalyticsV2ElementType.Client && arg.Container.Segment !== AnalyticsV2ElementType.Project) {
        return
      }

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const http_url = "Analytics/MultiEdit/RemoveParameterInstances";

        // console.log(arg)
        const response = await http.put(http_url, arg);
        // console.log(response)
        if (response.status === 200) {
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * addParameters method. Backend API method for adding parameters to entites
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async addParameters(arg: MultiEditParameterListAsker, no_indication: boolean = false) {
      if (arg.Container.Segment !== AnalyticsV2ElementType.Client && arg.Container.Segment !== AnalyticsV2ElementType.Project) {
        return
      }

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const http_url = "Analytics/MultiEdit/AddParameterInstances";

        // console.log(arg)
        const response = await http.put(http_url, arg);
        // console.log(response)
        if (response.status === 200) {
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },

    /**
     * editParameters method. Backend API method for setting existing parameters values
     * @param arg - item parameters and item Id
     * @returns object with response_ok if successful, error otherwise
     */
    async editParameters(arg: MultiEditParameterValueEditAsker, no_indication: boolean = false) {
      if (arg.Container.Segment !== AnalyticsV2ElementType.Client && arg.Container.Segment !== AnalyticsV2ElementType.Project
         && arg.Container.Segment !== AnalyticsV2ElementType.StorageItem
      ) {
        return
      }

      const rootstore = rootStore()
      const http = new CommonHttp()
      try {
        const http_url = "Analytics/MultiEdit/EditValuesOfInstances";

        // console.log(arg)
        const response = await http.put(http_url, arg);
        // console.log(response)
        if (response.status === 200) {
          return { response_ok: true }
        }
      } catch (e) {
        console.log(JSON.stringify(e))
      }

      return { response_ok: false }
    },
  },

  getters: {
  }
});
